import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import TagsCell from "@via/components/tags-cell.tsx";
import DataCollectionFilter, {
  PremiumDatasetViewType,
} from "@via/pages/data/data-collections/components/data-collection-filter.tsx";
import {
  PREMIUM_DATASET_TABLE,
  EMPTY_TABLE_MESSAGE,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { type TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { VmetaDataSetResponse } from "common";
import { type JSX } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";

const columnHelper = createColumnHelper<VmetaDataSetResponse>();

interface PremiumDatasetTableProps {
  data: VmetaDataSetResponse[] | undefined;
  isLoading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  sort: Array<{ id: string; desc: boolean }>;
  setSort: (sort: Array<{ id: string; desc: boolean }>) => void;
  columnFilters: Array<{ id: string; value: unknown }>;
  setColumnFilters: (
    filters: (
      prevFilters: Array<{ id: string; value: unknown }>
    ) => Array<{ id: string; value: unknown }>
  ) => void;
  premiumDatasetViewType?: PremiumDatasetViewType;
  onPremiumDatasetViewTypeChange?: (type: PremiumDatasetViewType) => void;
  total: number | undefined;
  // Selection props for InputFileModal
  selectedDataset?: VmetaDataSetResponse | null;
  onDatasetSelect?: (dataset: VmetaDataSetResponse) => void;
  // Action props for DataCollectionList
  onAction?: (
    option: TableContextMenuInterface,
    collection: VmetaDataSetResponse
  ) => void;
  // Mode to determine behavior
  mode: "selection" | "management";
}

export default function PremiumDatasetTable({
  data,
  isLoading,
  page,
  setPage,
  pageSize,
  setPageSize,
  sort,
  setSort,
  columnFilters,
  setColumnFilters,
  premiumDatasetViewType,
  onPremiumDatasetViewTypeChange,
  total,
  selectedDataset,
  onDatasetSelect,
  onAction,
  mode,
}: PremiumDatasetTableProps): JSX.Element {
  const columns = [
    // Show select column at the beginning for selection mode
    ...(mode === "selection"
      ? [
          columnHelper.accessor("_id", {
            cell: info => {
              const isSelected =
                selectedDataset?._id === info.row.original?._id;
              return (
                <div className="flex items-center">
                  {isSelected ? (
                    <span className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-indigo-50 bg-green-500 border border-green-200 rounded-md whitespace-nowrap hover:bg-green-600 hover:border-indigo-300 transition-colors duration-200 cursor-not-allowed">
                      <CheckIcon className="w-4 h-4 -ml-1 shrink-0" />
                      Selected
                    </span>
                  ) : (
                    <button
                      onClick={() => onDatasetSelect?.(info.row.original)}
                      className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md whitespace-nowrap hover:bg-indigo-100 hover:border-indigo-300 transition-colors duration-200">
                      Select
                    </button>
                  )}
                </div>
              );
            },
            header: () => "",
            enableSorting: false,
            size: 128,
            minSize: 128,
            maxSize: 128,
          }),
        ]
      : []),
    columnHelper.accessor("name", {
      cell: info => (
        <Link
          to={`/data/collections/${info.row.original?._id?.toString()}`}
          className="via-link"
          target={mode === "selection" ? "_blank" : undefined}>
          {info.getValue()}
        </Link>
      ),
      header: () => <span>Name</span>,
    }),
    columnHelper.accessor("fileCount", {
      cell: info => {
        const value = info.getValue();
        return <span>{value || 0}</span>;
      },
      enableSorting: false,
      header: () => <span>File Count</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LL")}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("updatedAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LL")}</span>;
      },
      header: () => <span>Last Updated</span>,
    }),
    columnHelper.accessor("owner", {
      cell: info => {
        const owner = info.getValue();
        return <span>{owner?.name}</span>;
      },
      header: () => <span>Owner</span>,
    }),
    columnHelper.display({
      id: "tags",
      cell: ({ row }) => (
        <TagsCell
          tags={row.original.tags ?? []}
          rowId={String(row.original._id)}
        />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    // Show options column at the end for management mode
    ...(mode === "management"
      ? [
          columnHelper.accessor("_id", {
            cell: info => (
              <TableContextMenu
                options={PREMIUM_DATASET_TABLE}
                onSelect={option => {
                  onAction?.(option, info.row.original);
                }}
              />
            ),
            header: () => "Options",
            enableSorting: false,
          }),
        ]
      : []),
  ] as Array<ColumnDef<VmetaDataSetResponse>>;

  return (
    <Table
      isLoading={isLoading}
      data={data}
      columns={columns}
      sort={sort}
      setSort={setSort}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      total={total}
      size={TableSize.Big}
      emptyMessage={EMPTY_TABLE_MESSAGE.DATASETS}
      applyColumnSize={mode === "selection"}
      additionalFilters={
        <DataCollectionFilter
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
          setPage={setPage}
          setPageSize={setPageSize}
          showOwnerNameFilter
          showPremiumDatasetViewButtons={true}
          premiumDatasetViewType={premiumDatasetViewType}
          onPremiumDatasetViewTypeChange={onPremiumDatasetViewTypeChange}
        />
      }
    />
  );
}
